import { ChangeDetectionStrategy, Component, HostListener, OnInit, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, debounceTime, distinctUntilChanged } from 'rxjs';

import { CpfModeComponent } from './cpf-mode/cpf-mode.component';
import { ConferenciaService } from '../../../core/conferencia/conferencia.service';
import { AtendimentoConferencia, CupomConferenciaItem, CupomDisponivel, CupomSugestao, RegraStatus, UsoPorDia } from '../../../core/conferencia/conferencia.model';
import {
  CupomService,
  CupomEstatisticas,
  CupomAplicacaoRecente,
} from '../../../core/cupom/cupom.service';
import { CuponsRecentesService } from '../../../core/cupons-recentes/cupons-recentes.service';
import { AuthService } from '../../../core/auth/auth.service';

type Modo = 'cpf' | 'cupom' | 'atendimento';

@Component({
  selector: 'app-cupom-conferencia',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule, CpfModeComponent],
  templateUrl: './cupom-conferencia.component.html',
  styleUrl: './cupom-conferencia.component.scss',
})
export class CupomConferenciaComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly confSvc = inject(ConferenciaService);
  private readonly cupomSvc = inject(CupomService);
  private readonly recentesSvc = inject(CuponsRecentesService);

  protected readonly isAdmin = inject(AuthService).isAdmin;

  protected readonly modo = signal<Modo>('cupom');
  protected readonly qInicial = signal<string>('');

  protected readonly searchTerm = signal<string>('');
  protected readonly suggestions = signal<CupomSugestao[]>([]);
  protected readonly showDropdown = signal(false);
  protected readonly loadingSuggest = signal(false);
  protected readonly cupomSelecionado = signal<CupomSugestao | null>(null);
  protected readonly erroSelecao = signal<string | null>(null);

  protected readonly estatisticas = signal<CupomEstatisticas | null>(null);
  protected readonly aplicacoes = signal<CupomAplicacaoRecente[]>([]);
  protected readonly loadingDados = signal(false);
  protected readonly erroDados = signal<string | null>(null);

  protected atendimentoInput = '';
  protected readonly atendimentoResultado = signal<AtendimentoConferencia | null>(null);
  protected readonly loadingAtendimento = signal(false);
  protected readonly confirmarAplicarId = signal<number | null>(null);
  protected readonly aplicandoId = signal<number | null>(null);
  protected readonly feedbackAplicacao = signal<{ tipo: 'ok' | 'erro'; texto: string } | null>(null);
  protected readonly erroAtendimento = signal<string | null>(null);

  protected readonly chartDias = signal(30);
  protected readonly chartPontos = signal<UsoPorDia[]>([]);
  protected readonly loadingChart = signal(false);

  protected readonly regrasCupom = signal<RegraStatus[]>([]);

  protected readonly chartMax = computed(() => {
    const pts = this.chartPontos();
    return pts.reduce((m, p) => Math.max(m, p.quantidade), 0);
  });

  protected readonly chartLegenda = computed(() => {
    const d = this.chartDias();
    if (d === 7) return 'ultimos 7 dias';
    if (d === 30) return 'ultimos 30 dias';
    if (d === 90) return 'ultimos 90 dias';
    return 'ultimos ' + d + ' dias';
  });

  protected readonly historicoAberto = signal(false);
  protected historicoDataIni = '';
  protected historicoDataFim = '';
  protected readonly historicoCdEstab = signal<number | null>(null);
  protected readonly historicoItens = signal<CupomConferenciaItem[]>([]);
  protected readonly historicoTotal = signal(0);
  protected readonly historicoPagina = signal(1);
  protected readonly historicoLoading = signal(false);
  protected readonly historicoErro = signal<string | null>(null);
  protected readonly historicoTake = 20;

  protected readonly historicoTotalPaginas = computed(() => {
    return Math.max(1, Math.ceil(this.historicoTotal() / this.historicoTake));
  });

  protected readonly ticketMedio = computed(() => {
    const s = this.estatisticas();
    if (!s || s.totalUsados <= 0) return 0;
    return s.totalDescontado / s.totalUsados;
  });

  protected readonly maiorUsoUnidade = computed(() => {
    const por = this.estatisticas()?.porUnidade ?? [];
    return por.reduce((max, u) => Math.max(max, u.totalUsos), 0);
  });

  private readonly searchInput$ = new Subject<string>();

  ngOnInit(): void {
    this.searchInput$
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe(termo => this.executarAutocomplete(termo));

    this.route.queryParamMap.subscribe(qp => this.sincronizarUrl(qp));
  }

  private sincronizarUrl(qp: import('@angular/router').ParamMap): void {
    const modoRaw = qp.get('modo');
    let novoModo: Modo = modoRaw === 'cpf' ? 'cpf' : modoRaw === 'atendimento' ? 'atendimento' : 'cupom';
    if (!this.isAdmin() && novoModo === 'cupom') {
      novoModo = 'cpf';
    }
    const novoQ = qp.get('q') ?? '';

    if (this.modo() !== novoModo) {
      this.modo.set(novoModo);
      this.limparEstadoCupom();
      this.limparEstadoAtendimento();
    }

    this.qInicial.set(novoQ);

    if (novoModo === 'cupom') {
      if (novoQ.length >= 4 && this.cupomSelecionado()?.codCupom !== novoQ) {
        this.confSvc.buscarCupons(novoQ, 1).subscribe({
          next: res => {
            const primeiro = res?.data?.[0];
            if (primeiro) {
              this.aplicarSelecao(primeiro);
            } else {
              this.erroSelecao.set(`Cupom "${novoQ}" nao encontrado.`);
            }
          },
          error: () => this.erroSelecao.set('Falha ao buscar cupom.'),
        });
      } else if (!novoQ && this.cupomSelecionado()) {
        this.limparEstadoCupom();
      }
    } else if (novoModo === 'atendimento' && novoQ) {
      const nr = Number(novoQ);
      if (!isNaN(nr) && nr > 0 && this.atendimentoInput !== String(nr)) {
        this.atendimentoInput = String(nr);
        this.buscarAtendimento();
      }
    }
  }

  private limparEstadoCupom(): void {
    this.cupomSelecionado.set(null);
    this.searchTerm.set('');
    this.suggestions.set([]);
    this.showDropdown.set(false);
    this.estatisticas.set(null);
    this.aplicacoes.set([]);
    this.chartPontos.set([]);
    this.regrasCupom.set([]);
    this.historicoAberto.set(false);
    this.historicoDataIni = '';
    this.historicoDataFim = '';
    this.historicoCdEstab.set(null);
    this.historicoItens.set([]);
    this.historicoTotal.set(0);
    this.historicoPagina.set(1);
    this.erroSelecao.set(null);
    this.erroDados.set(null);
  }

  private limparEstadoAtendimento(): void {
    this.atendimentoInput = '';
    this.atendimentoResultado.set(null);
    this.erroAtendimento.set(null);
  }

  protected setModo(m: Modo): void {
    if (m === 'cupom' && !this.isAdmin()) return;
    if (this.modo() === m) return;

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { modo: m, q: null, status: null, periodoDias: null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  protected onCpfQChange(q: string): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { q: q || null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  protected onSearchTermChange(value: string): void {
    this.searchTerm.set(value);
    this.searchInput$.next(value);
    this.showDropdown.set(true);
    this.erroSelecao.set(null);
  }

  private executarAutocomplete(termo: string): void {
    if (termo.length < 3) {
      this.suggestions.set([]);
      this.loadingSuggest.set(false);
      return;
    }
    this.loadingSuggest.set(true);
    this.confSvc.buscarCupons(termo, 10).subscribe({
      next: res => {
        this.suggestions.set(res?.data ?? []);
        this.loadingSuggest.set(false);
      },
      error: () => {
        this.suggestions.set([]);
        this.loadingSuggest.set(false);
      },
    });
  }

  protected selecionar(s: CupomSugestao): void {
    this.recentesSvc.registrar(s.codCupom, s.nrSequencia);
    this.aplicarSelecao(s);
  }

  private aplicarSelecao(s: CupomSugestao): void {
    this.cupomSelecionado.set(s);
    this.searchTerm.set(s.codCupom);
    this.showDropdown.set(false);
    this.estatisticas.set(null);
    this.aplicacoes.set([]);
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { q: s.codCupom },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
    this.carregarDados(s.nrSequencia);
  }

  protected trocarCupom(): void {
    this.cupomSelecionado.set(null);
    this.searchTerm.set('');
    this.suggestions.set([]);
    this.estatisticas.set(null);
    this.aplicacoes.set([]);
    this.chartPontos.set([]);
    this.regrasCupom.set([]);
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { q: null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  private carregarDados(nrSequencia: number): void {
    this.loadingDados.set(true);
    this.erroDados.set(null);

    this.cupomSvc.estatisticas(nrSequencia).subscribe({
      next: res => this.estatisticas.set(res?.data ?? null),
      error: e => this.erroDados.set(`Falha ao buscar estatisticas: ${e.status ?? ''}`),
    });

    this.cupomSvc.ultimasAplicacoes(nrSequencia, 5).subscribe({
      next: res => {
        this.aplicacoes.set(res?.data ?? []);
        this.loadingDados.set(false);
      },
      error: e => {
        this.erroDados.set(`Falha ao buscar aplicacoes: ${e.status ?? ''}`);
        this.loadingDados.set(false);
      },
    });

    this.carregarChart(nrSequencia, this.chartDias());
    this.carregarRegras(nrSequencia);
  }

  private carregarRegras(nrSequencia: number): void {
    this.confSvc.regrasCupom(nrSequencia).subscribe({
      next: res => this.regrasCupom.set(res?.data ?? []),
      error: () => this.regrasCupom.set([]),
    });
  }

  private carregarChart(nrSequencia: number, dias: number): void {
    this.loadingChart.set(true);
    const agruparPor = this.agruparPorParaDias(dias);
    this.confSvc.usosPorDia(nrSequencia, dias, undefined, undefined, agruparPor).subscribe({
      next: res => {
        this.chartPontos.set(res?.data?.pontos ?? []);
        this.loadingChart.set(false);
      },
      error: () => {
        this.chartPontos.set([]);
        this.loadingChart.set(false);
      },
    });
  }

  private agruparPorParaDias(dias: number): 'dia' | 'semana' | 'mes' {
    if (dias <= 31) return 'dia';
    if (dias <= 100) return 'semana';
    return 'mes';
  }

  protected setChartDias(d: number): void {
    if (this.chartDias() === d) return;
    this.chartDias.set(d);
    const sel = this.cupomSelecionado();
    if (sel) this.carregarChart(sel.nrSequencia, d);
  }

  protected ytdDias(): number {
    const now = new Date();
    const inicioAno = new Date(now.getFullYear(), 0, 1);
    return Math.ceil((now.getTime() - inicioAno.getTime()) / 86400000);
  }

  protected barAltura(p: UsoPorDia): number {
    const max = this.chartMax();
    if (max <= 0) return 0;
    return (p.quantidade / max) * 100;
  }

  protected fmtBarLabel(iso: string): string {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
  }

  protected fmtBarTitle(p: UsoPorDia): string {
    const d = new Date(p.data);
    if (isNaN(d.getTime())) return String(p.quantidade);
    return `${d.toLocaleDateString('pt-BR')}: ${p.quantidade} uso(s)`;
  }

  protected toggleHistorico(): void {
    const novoEstado = !this.historicoAberto();
    this.historicoAberto.set(novoEstado);

    if (novoEstado && !this.historicoDataIni) {
      const hoje = new Date();
      const seisMesesAtras = new Date();
      seisMesesAtras.setMonth(hoje.getMonth() - 6);
      this.historicoDataFim = hoje.toISOString().slice(0, 10);
      this.historicoDataIni = seisMesesAtras.toISOString().slice(0, 10);
      this.historicoPagina.set(1);
      this.aplicarHistorico();
    }
  }

  protected aplicarHistorico(): void {
    const sel = this.cupomSelecionado();
    if (!sel) return;
    if (!this.historicoDataIni || !this.historicoDataFim) {
      this.historicoErro.set('Informe as duas datas.');
      return;
    }
    if (this.historicoDataIni > this.historicoDataFim) {
      this.historicoErro.set('Data inicial nao pode ser maior que a final.');
      return;
    }
    this.historicoErro.set(null);
    this.historicoLoading.set(true);

    const filtros = {
      status: 'Usado' as const,
      dataIni: this.historicoDataIni,
      dataFim: this.historicoDataFim,
      cdEstabelecimento: this.historicoCdEstab(),
      skip: (this.historicoPagina() - 1) * this.historicoTake,
      take: this.historicoTake,
    };

    this.confSvc.listarPorCupom(sel.nrSequencia, filtros).subscribe({
      next: res => {
        this.historicoItens.set(res?.data?.dados ?? []);
        this.historicoTotal.set(res?.data?.total ?? 0);
        this.historicoLoading.set(false);
      },
      error: e => {
        this.historicoErro.set(`Falha ao buscar historico: ${e.status ?? ''}`);
        this.historicoLoading.set(false);
      },
    });
  }

  protected mudarPaginaHistorico(p: number): void {
    if (p < 1 || p > this.historicoTotalPaginas()) return;
    this.historicoPagina.set(p);
    this.aplicarHistorico();
  }

  protected readonly Math = Math;

  @HostListener('document:click', ['$event'])
  protected onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.combobox-wrap')) {
      this.showDropdown.set(false);
    }
  }

  protected tipoLabel(ie: string): string {
    return ({ C: 'B2C', B: 'B2B', E: 'Empresa', M: 'Misto', P: 'Todos' } as Record<string, string>)[ie] ?? ie;
  }

  protected descontoLabel(s: CupomSugestao): string {
    if (s.ieTipoDesconto === 'P' && s.prDesconto != null) return `Desconto ${s.prDesconto}%`;
    if ((s.ieTipoDesconto === 'F' || s.ieTipoDesconto === 'R') && s.vlDesconto != null) return `Desconto fixo ${this.fmtR(s.vlDesconto)}`;
    if (s.ieTipoDesconto === 'V') return 'Voucher';
    return 'Desconto configurado';
  }

  protected descontoOff(s: CupomSugestao): string {
    if (s.ieTipoDesconto === 'P' && s.prDesconto != null) return `${s.prDesconto}% OFF`;
    if ((s.ieTipoDesconto === 'F' || s.ieTipoDesconto === 'R') && s.vlDesconto != null) return `${this.fmtR(s.vlDesconto)} OFF`;
    return '';
  }

  protected fmtDt(iso: string | null | undefined): string {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' });
  }

  protected fmtDtUso(iso: string): string {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    const hoje = new Date();
    const ontem = new Date(hoje);
    ontem.setDate(hoje.getDate() - 1);
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    if (d.toDateString() === hoje.toDateString()) return `Hoje, ${hh}:${mm}`;
    if (d.toDateString() === ontem.toDateString()) return `Ontem, ${hh}:${mm}`;
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }) + ` ${hh}:${mm}`;
  }

  protected fmtR(v: number): string {
    return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  }

  protected fmtN(v: number): string {
    return v.toLocaleString('pt-BR');
  }

  protected buscarAtendimento(manterFeedback = false): void {
    const nr = Number(this.atendimentoInput.replace(/\D/g, ''));
    if (!nr || nr <= 0) {
      this.erroAtendimento.set('Informe um numero de atendimento valido.');
      return;
    }
    this.loadingAtendimento.set(true);
    this.erroAtendimento.set(null);
    this.atendimentoResultado.set(null);
    this.confirmarAplicarId.set(null);
    if (manterFeedback !== true) this.feedbackAplicacao.set(null);

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { q: String(nr) },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });

    this.confSvc.buscarPorAtendimento(nr).subscribe({
      next: res => {
        this.atendimentoResultado.set(res?.data ?? null);
        this.loadingAtendimento.set(false);
      },
      error: e => {
        this.erroAtendimento.set(`Falha ao buscar atendimento: ${e.status ?? ''} ${e.error?.error?.message ?? e.message ?? ''}`);
        this.loadingAtendimento.set(false);
      },
    });
  }

  /** O cupom do cartao (NAO_APLICADO) esta na lista e pode ser aplicado? */
  protected cartaoPodeAplicar(res: AtendimentoConferencia): boolean {
    const nr = res.cupomAplicado?.nrSeqCupomPaciente;
    return !!(res.cuponsDisponiveis ?? []).find(c => c.nrSeqCupomPaciente === nr)?.podeAplicar;
  }

  protected pedirAplicar(c: CupomDisponivel): void {
    this.feedbackAplicacao.set(null);
    this.confirmarAplicarId.set(c.nrSeqCupomPaciente);
  }

  protected cancelarAplicar(): void {
    this.confirmarAplicarId.set(null);
  }

  protected confirmarAplicar(res: AtendimentoConferencia, c: CupomDisponivel): void {
    if (this.aplicandoId() !== null) return;
    this.aplicandoId.set(c.nrSeqCupomPaciente);
    this.confSvc.aplicarNoAtendimento(res.nrAtendimento, c.nrSeqCupomPaciente).subscribe({
      next: r => {
        this.aplicandoId.set(null);
        this.confirmarAplicarId.set(null);
        this.feedbackAplicacao.set({ tipo: 'ok', texto: r?.data?.mensagem ?? `Cupom ${c.codCupom} aplicado.` });
        this.atendimentoInput = String(res.nrAtendimento);
        this.buscarAtendimento(true);
      },
      error: e => {
        this.aplicandoId.set(null);
        this.confirmarAplicarId.set(null);
        this.feedbackAplicacao.set({ tipo: 'erro', texto: e?.error?.error?.message ?? 'Falha ao aplicar o cupom.' });
        // A proc pode ter commitado mesmo com erro de transporte: recarrega o estado real.
        this.atendimentoInput = String(res.nrAtendimento);
        this.buscarAtendimento(true);
      },
    });
  }

  protected limparAtendimento(): void {
    this.feedbackAplicacao.set(null);
    this.confirmarAplicarId.set(null);
    this.atendimentoInput = '';
    this.atendimentoResultado.set(null);
    this.erroAtendimento.set(null);
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { q: null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }
}
